// ─── Getting a location good enough to stamp a step with ─────────────────────
//
// A step recorded from a driver's phone is evidence, and the whole of its value
// is the pairing: this person, at this time, *at this place*. Without the third
// the record says a box was collected at 11:58 by somebody who may have been at
// the terminal and may have been at home, and there is no way to tell afterwards
// which — the two are the same document.
//
// It used to be best-effort, and best-effort meant absent. The old helper gave
// the browser five seconds, resolved `{}` on failure and let the step go through
// without coordinates; at a container terminal, surrounded by steel and often
// under a gantry, five seconds is not enough for a cold fix. So the stamps that
// existed were the easy ones — a driver in an open yard on a clear day — and the
// ones anybody would actually go looking for were the ones that were not there.
//
// This asks properly and reports what happened. It never invents a position and
// it never silently gives up: every outcome is a value the caller has to handle.

/** A fix good enough to send. */
export interface GeoFix {
  latitude: number;
  longitude: number;
  /**
   * The radius the fix is good to, in metres, when the phone stated one.
   *
   * Passed through rather than enforced. A generous fix is still a fix, and
   * refusing one would take the commonest honest reading — a cold GPS in a yard
   * full of containers — and treat it as an absence, which is the exact failure
   * this file exists to end. It is recorded because two stamps that both read
   * "at the terminal" are different evidence when one is good to 8 m and the
   * other to 3 km, and by the time anyone reads them the phone is long gone.
   */
  accuracy?: number;
}

/**
 * Why there is no fix.
 *
 * Three outcomes and not one, because the driver's next move differs for each
 * and only the first is something they can fix themselves:
 *
 *   denied       location is switched off for this app. Fixable in settings,
 *                and worth saying so — it is by far the commonest cause and the
 *                only one where "try again" is useless advice.
 *   unavailable  the phone cannot get a fix here at all.
 *   timeout      it was still trying when the clock ran out. Often the same
 *                thing as `unavailable` in practice, but a retry genuinely
 *                helps here and genuinely does not there.
 *
 * These codes are what the notify-dispatch request carries, so the office is
 * told which of the three it is rather than "no location".
 */
export type GeoFailure = "denied" | "unavailable" | "timeout";

export type GeoResult =
  | { ok: true; fix: GeoFix }
  | { ok: false; reason: GeoFailure };

/**
 * How long to wait for a fix.
 *
 * Twenty seconds rather than five. A warm GPS answers in under a second and is
 * unaffected by this; a cold one in a container yard routinely takes ten to
 * fifteen, and the old five-second ceiling was landing squarely inside the
 * range where a real fix was on its way. The cost of waiting is a spinner the
 * driver is already watching. The cost of not waiting was the missing stamps.
 */
const TIMEOUT_MS = 20_000;

/**
 * How stale a cached fix may be.
 *
 * Thirty seconds. Long enough that pressing "I'm here" and then "confirm and
 * continue" a moment later does not re-acquire from scratch, short enough that
 * a cached position can never be from the previous stop — which is the one
 * error that would be invisible in the record and completely wrong.
 */
const MAX_AGE_MS = 30_000;

/** The browser's numeric error codes, which are not named constants at runtime. */
const FAILURE_BY_CODE: Record<number, GeoFailure> = {
  1: "denied", // PERMISSION_DENIED
  2: "unavailable", // POSITION_UNAVAILABLE
  3: "timeout", // TIMEOUT
};

/**
 * Ask the phone where it is, once.
 *
 * `enableHighAccuracy` is on. It costs battery and it is the difference between
 * a GPS fix and one derived from whichever cell mast the handset is talking to,
 * and a cell-tower fix in a port district can be a kilometre out — which is to
 * say, it can place a truck at the wrong terminal. For a stamp that exists to
 * be evidence, the wrong answer arriving quickly is not the trade to make.
 */
export function requestGeoFix(): Promise<GeoResult> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      return resolve({ ok: false, reason: "unavailable" });
    }

    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          ok: true,
          fix: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: Number.isFinite(position.coords.accuracy)
              ? position.coords.accuracy
              : undefined,
          },
        }),
      (error) =>
        resolve({
          ok: false,
          reason: FAILURE_BY_CODE[error.code] ?? "unavailable",
        }),
      {
        enableHighAccuracy: true,
        timeout: TIMEOUT_MS,
        maximumAge: MAX_AGE_MS,
      },
    );
  });
}

/**
 * Whether asking again could plausibly help.
 *
 * A denied permission cannot be retried into working — the browser will refuse
 * from cache without even prompting — so offering "try again" there is offering
 * a button that does nothing. The other two are worth one more attempt: a fix
 * that timed out was often seconds away, and an unavailable one changes when the
 * driver walks out from under a gantry.
 */
export const isRetryable = (reason: GeoFailure) => reason !== "denied";
